import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { ClockIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';

export default function AdminHours() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const res = await axios.get('/hours/admin/statistics');
                setStats(res.data.data);
            } catch (err) {
                toast.error(err.response?.data?.message || 'Unable to load hour statistics');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const statusCounts = stats?.statusCounts || [];
    const templates = stats?.templateProgress || [];

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Hours Management"
                description="Program-wide hour overview from live statistics. Mentors approve hours from the Mentor Dashboard."
                actions={
                    <Link className="btn-secondary" to="/mentor/dashboard">
                        Mentor Dashboard
                    </Link>
                }
            />

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="status">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton h-24" />
                    ))}
                    <span className="sr-only">Loading…</span>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {statusCounts.map((row, i) => (
                            <div
                                key={row.status}
                                className={`kpi-card animate-fade-up stagger-${Math.min(6, i + 1)}`}
                            >
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">{row.status}</p>
                                <p className="text-xl font-bold mt-2 text-ink-950 tabular-nums">{row.count} entries</p>
                                <p className="text-xs text-ink-500 mt-1">
                                    {Number(row.total_hours || 0).toFixed(1)} logged ·{' '}
                                    {Number(row.approved_hours || 0).toFixed(1)} approved
                                </p>
                            </div>
                        ))}
                        {statusCounts.length === 0 && (
                            <EmptyState
                                icon={ClockIcon}
                                title="No hour entries yet"
                                description="Hour statistics will appear as interns log practical hours."
                                className="sm:col-span-2 lg:col-span-3"
                            />
                        )}
                    </div>

                    {templates.length > 0 && (
                        <section className="card animate-fade-up">
                            <h2 className="text-base font-semibold text-ink-950">
                                Template progress
                            </h2>
                            <ul className="mt-3 divide-y divide-border">
                                {templates.map((t) => (
                                    <li
                                        key={t.id}
                                        className="py-3 flex justify-between text-sm gap-4 hover:bg-ink-50/50 -mx-2 px-2 rounded-lg transition-colors"
                                    >
                                        <span className="font-medium text-ink-800">{t.name}</span>
                                        <span className="text-ink-500 tabular-nums">
                                            {t.intern_count} interns · avg{' '}
                                            {Number(t.avg_progress || 0).toFixed(0)}%
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
